import { CartLinesUpdateEvent } from '@shopify/events';

class EveryteeProduct extends HTMLElement {
  async connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.model = await import(this.dataset.modelUrl);
    this.variants = JSON.parse(this.querySelector('[data-et-product]').textContent);
    this.messages = JSON.parse(this.querySelector('[data-et-messages]').textContent);
    this.form = this.querySelector('form');
    this.submit = this.querySelector('[data-et-submit]');
    this.status = this.querySelector('[data-et-status]');
    this.fileInput = this.querySelector('[data-logo-input]');
    this.personalizationIndex = Array.from(this.querySelectorAll('fieldset')).findIndex((el) => el.dataset.optionKind === 'personalization');
    this.form.addEventListener('change', (event) => {
      if (event.target.matches('input[type=radio]')) this.updateVariant();
    });
    this.fileInput.addEventListener('change', () => this.previewFile());
    this.fileInput.addEventListener('invalid', () => {
      if (!this.fileInput.files.length && !this.fileInput.validity.customError) {
        this.fileInput.setCustomValidity(this.messages.missing);
        this.status.textContent = this.messages.missing;
      }
    });
    this.querySelector('[data-logo-remove]').addEventListener('click', () => this.clearLogo());
    this.querySelectorAll('[data-full-src]').forEach((button) => button.addEventListener('click', () => this.showImage(button)));
    this.form.addEventListener('submit', (event) => this.addToCart(event));
    this.updateVariant(false);
  }

  disconnectedCallback() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  }

  get options() {
    return Array.from(this.querySelectorAll('fieldset')).map((fieldset) => fieldset.querySelector('input:checked')?.value || '');
  }

  updateVariant(updateUrl = true) {
    const selected = this.options;
    this.variant = this.model.findVariant(this.variants, selected);
    const personalized = this.querySelector('[data-logo-choice="true"]:checked') !== null;
    this.querySelector('[data-logo-panel]').hidden = !personalized;
    this.fileInput.disabled = !personalized;
    this.fileInput.required = personalized;
    this.querySelector('[data-print-placement]').disabled = !personalized;
    if (!personalized) this.clearLogo();
    this.querySelectorAll('fieldset').forEach((fieldset, index) => {
      const values = this.model.availableValues(this.variants, selected, index);
      fieldset.querySelectorAll('input[type=radio]').forEach((input) => {
        // Preserve navigation between colours even when their size ranges differ.
        const possible = index === 0 ? this.variants.some((variant) => variant.options[0] === input.value && variant.available) : values.has(input.value);
        input.disabled = !possible;
        input.closest('label').classList.toggle('is-unavailable', !possible);
      });
    });
    this.submit.disabled = !this.variant?.available || this.busy || this.decodingLogo;
    this.submit.textContent = this.variant?.available ? this.messages.add : this.messages.unavailable;
    this.status.textContent = '';
    this.querySelector('[data-et-variant]').value = this.variant?.id || '';
    if (this.variant) {
      this.querySelector('[data-et-price]').textContent = this.model.money(this.variant.price, this.dataset.locale, this.dataset.currency);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.variant.id);
        history.replaceState({}, '', url);
      }
      if (this.personalizationIndex >= 0) {
        const {plain,logo} = this.model.personalizationPair(this.variants,selected,this.personalizationIndex);
        const supplement = this.querySelector('[data-logo-supplement]');
        if (supplement && plain && logo) supplement.textContent = `+${this.model.money(logo.price - plain.price, this.dataset.locale, this.dataset.currency)}`;
      }
    }
    this.querySelector('[data-selected-color]').textContent = selected[0];
    const thumbnails = Array.from(this.querySelectorAll('[data-full-src]'));
    const colorId = this.querySelector('fieldset input:checked')?.dataset.optionValueId;
    thumbnails.forEach((thumbnail) => { thumbnail.hidden = !!thumbnail.dataset.colorId && thumbnail.dataset.colorId !== colorId; });
    const selectedImage = thumbnails.find((thumbnail) => thumbnail.dataset.imageId === String(this.variant?.featured_image?.id));
    const firstVisible = selectedImage || thumbnails.find((thumbnail) => !thumbnail.hidden);
    if (firstVisible) this.showImage(firstVisible);
  }

  showImage(button) {
    const image = this.querySelector('.et-main-image');
    // Keep responsive loading after every color/thumbnail change, including init.
    const url = new URL(button.dataset.fullSrc, window.location.href);
    image.srcset = [480, 720, 960, 1200, 1600].map(width => {
      const source = new URL(url);
      source.searchParams.set('width', width);
      return `${source.href} ${width}w`;
    }).join(', ');
    image.src = button.dataset.fullSrc;
    image.alt = button.dataset.imageAlt || this.querySelector('h1').textContent;
    this.querySelectorAll('[data-full-src]').forEach((thumbnail) => thumbnail.setAttribute('aria-pressed', String(thumbnail === button)));
  }

  clearLogo() {
    this.decodingLogo = false;
    this.submit.disabled = !this.variant?.available || this.busy;
    this.fileInput.value = '';
    this.fileInput.setCustomValidity('');
    this.querySelector('[data-logo-preview]').hidden = true;
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }

  async previewFile() {
    const file = this.fileInput.files[0];
    if (!file) { this.clearLogo(); return; }
    const error = this.model.validateLogo(file);
    if (error) {
      this.clearLogo();
      this.status.textContent = this.messages[error];
      this.fileInput.setCustomValidity(this.messages[error]);
      this.fileInput.reportValidity();
      return;
    }
    this.fileInput.setCustomValidity('');
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    const url = URL.createObjectURL(file);
    this.previewUrl = url;
    this.decodingLogo = true;
    this.submit.disabled = true;
    const image = this.querySelector('[data-logo-image]');
    image.src = url;
    try { await image.decode(); }
    catch {
      if (this.previewUrl !== url) return;
      this.clearLogo();
      this.submit.disabled = !this.variant?.available || this.busy;
      this.status.textContent = this.messages.type;
      return;
    }
    if (this.previewUrl !== url) return;
    this.decodingLogo = false;
    this.submit.disabled = !this.variant?.available || this.busy;
    this.querySelector('[data-logo-name]').textContent = file.name;
    this.querySelector('[data-logo-preview]').hidden = false;
    this.status.textContent = '';
  }

  async addToCart(event) {
    event.preventDefault();
    if (this.busy || this.decodingLogo || !this.variant?.available) return;
    if (this.model.logoVariant(this.variant, this.personalizationIndex)) {
      const error = this.model.validateLogo(this.fileInput.files[0]);
      if (error) { this.status.textContent = this.messages[error]; this.fileInput.focus(); return; }
    }
    if (!this.form.reportValidity()) return;
    this.busy = true;
    this.submit.disabled = true;
    this.submit.textContent = this.messages.loading;
    this.status.textContent = '';
    const formData = new FormData(this.form);
    const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component')).map((el) => el.dataset.sectionId).filter(Boolean))].slice(0,5);
    formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({action:'add', context:'product', lines:[{merchandiseId:String(this.variant.id),quantity:Number(formData.get('quantity'))}], promise:deferred.promise}));
    try {
      const response = await fetch(this.dataset.cartAdd, {method:'POST', body:formData, headers:{Accept:'application/json'}, credentials:'same-origin'});
      const added = await response.json();
      if (!response.ok || added.status) {
        const message = typeof added.description === 'string' ? added.description : this.messages.unavailable;
        const cart = await fetch(`${this.dataset.cartUrl}.js`, {credentials:'same-origin'}).then((res) => res.json());
        deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{didError:true,items:cart.items,source:'product-form-component'}});
        this.status.textContent = message;
        this.busy = false;
        this.submit.disabled = false;
        this.submit.textContent = this.messages.add;
        return;
      }
      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, {credentials:'same-origin'});
      if (!cartResponse.ok) throw new Error('Cart confirmation unavailable');
      const cart = await cartResponse.json();
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{didError:false,items:cart.items,source:'product-form-component',sourceId:this.form.id,itemCount:Number(formData.get('quantity')),productId:this.dataset.productId,sections:added.sections}});
      this.status.textContent = this.messages.added;
      this.busy = false;
      this.submit.disabled = false;
      this.submit.textContent = this.messages.add;
    } catch (error) {
      deferred.reject(error);
      this.status.textContent = this.messages.network;
      this.querySelector('[data-recovery-link]').hidden = false;
      // A timed-out POST may have succeeded. Do not retry or enable duplicate submissions.
      this.submit.textContent = this.messages.add;
    }
  }
}

if (!customElements.get('everytee-product')) customElements.define('everytee-product', EveryteeProduct);
